// Mediterranean languages configuration for internationalization
import { SupportedLocale } from '../types';

export interface LanguageConfig {
  code: SupportedLocale;
  name: string;
  nativeName: string;
  flag: string;
  region: string;
  rtl: boolean;
}

// 5 Mediterranean languages as specified in the plan
export const MEDITERRANEAN_LANGUAGES: LanguageConfig[] = [
  {
    code: 'it-IT',
    name: 'language.names.it',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    region: 'language.regions.it',
    rtl: false,
  },
  {
    code: 'es-ES', 
    name: 'language.names.es',
    nativeName: 'Español',
    flag: '🇪🇸',
    region: 'language.regions.es',
    rtl: false,
  },
  {
    code: 'fr-FR',
    name: 'language.names.fr',
    nativeName: 'Français',
    flag: '🇫🇷',
    region: 'language.regions.fr',
    rtl: false,
  },
  {
    code: 'de-DE',
    name: 'language.names.de',
    nativeName: 'Deutsch', 
    flag: '🇩🇪',
    region: 'language.regions.de',
    rtl: false,
  },
  {
    code: 'en-GB',
    name: 'language.names.en',
    nativeName: 'English',
    flag: '🇬🇧', 
    region: 'language.regions.en',
    rtl: false,
  },
];

// Default language (English as universal fallback)
export const DEFAULT_LANGUAGE: SupportedLocale = 'en-GB';

// Language detection priority: User preference → Browser → Geo-location → Default
export const LANGUAGE_DETECTION_ORDER = [
  'localStorage', // User preference stored locally
  'navigator',    // Browser language
  'geo',         // Geo-location based (IP/GPS)
  'default'      // Fallback to DEFAULT_LANGUAGE
] as const;

// Language fallback chain
export const LANGUAGE_FALLBACKS: Record<SupportedLocale, SupportedLocale[]> = {
  'it-IT': ['en-GB', 'fr-FR', 'es-ES'],
  'es-ES': ['en-GB', 'fr-FR', 'it-IT'], 
  'fr-FR': ['en-GB', 'it-IT', 'es-ES'],
  'de-DE': ['en-GB', 'it-IT', 'fr-FR'],
  'en-GB': ['it-IT', 'fr-FR', 'es-ES'],
};

// Mediterranean locale formatting preferences
export const LOCALE_FORMATS: Record<SupportedLocale, {
  dateFormat: string;
  timeFormat: string;
  numberFormat: string;
  temperatureUnit: '°C' | '°F';
  distanceUnit: 'metric' | 'imperial';
  weightUnit: 'kg' | 'lb';
  lengthUnit: 'cm' | 'in';
}> = {
  'it-IT': {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: 'it-IT',
    temperatureUnit: '°C',
    distanceUnit: 'metric',
    weightUnit: 'kg',
    lengthUnit: 'cm',
  },
  'es-ES': {
    dateFormat: 'dd/MM/yyyy', 
    timeFormat: 'HH:mm',
    numberFormat: 'es-ES',
    temperatureUnit: '°C',
    distanceUnit: 'metric',
    weightUnit: 'kg',
    lengthUnit: 'cm',
  },
  'fr-FR': {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm', 
    numberFormat: 'fr-FR',
    temperatureUnit: '°C',
    distanceUnit: 'metric',
    weightUnit: 'kg',
    lengthUnit: 'cm',
  },
  'de-DE': {
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HH:mm',
    numberFormat: 'de-DE', 
    temperatureUnit: '°C',
    distanceUnit: 'metric',
    weightUnit: 'kg',
    lengthUnit: 'cm',
  },
  'en-GB': {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: 'en-GB',
    temperatureUnit: '°C',
    distanceUnit: 'metric',
    weightUnit: 'kg',
    lengthUnit: 'cm',
  },
};

// Helper functions
export const getLanguageByCode = (code: string): LanguageConfig | undefined => {
  return MEDITERRANEAN_LANGUAGES.find(lang => lang.code === code);
};

export const getLanguageNamesByCode = (): Record<SupportedLocale, string> => {
  return MEDITERRANEAN_LANGUAGES.reduce((acc, lang) => {
    acc[lang.code] = lang.nativeName;
    return acc;
  }, {} as Record<SupportedLocale, string>);
};

export const isValidLanguageCode = (code: string): code is SupportedLocale => {
  return MEDITERRANEAN_LANGUAGES.some(lang => lang.code === code);
};
